import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'yiren_favorites';
const EVENT_NAME = 'yiren_favorites_changed';

function readFavorites(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((item) => String(item)) : [];
  } catch {
    return [];
  }
}

function writeFavorites(list: string[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    window.dispatchEvent(new Event(EVENT_NAME));
  } catch {
    // ignore storage errors (e.g. private mode)
  }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const sync = () => setFavorites(readFavorites());
    window.addEventListener(EVENT_NAME, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT_NAME, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const isFavorite = useCallback(
    (id: string) => favorites.includes(String(id)),
    [favorites],
  );

  const toggleFavorite = useCallback((id: string) => {
    const key = String(id);
    const current = readFavorites();
    const next = current.includes(key)
      ? current.filter((item) => item !== key)
      : [...current, key];
    writeFavorites(next);
    return next.includes(key);
  }, []);

  return { favorites, isFavorite, toggleFavorite };
}